import React, { useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { OrderDetailsResponse } from '../api/orders';
import { toast } from 'react-hot-toast';

interface ReorderButtonProps {
  order: OrderDetailsResponse;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

const ReorderButton: React.FC<ReorderButtonProps> = ({
  order,
  variant = 'outlined',
  size = 'small',
  fullWidth = false
}) => {
  const { addToCart, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleReorder = async () => {
    if (!order || !order.items || order.items.length === 0) {
      toast.error('No items to reorder');
      return;
    }

    setLoading(true);
    
    try {
      // First clear the current cart
      clearCart();
      
      // Add each item from the order to the cart
      for (const item of order.items) {
        // Create a simplified product object that matches what addToCart expects
        const product = {
          id: item.productId,
          name: item.name,
          price: item.price,
          thumbnailUrl: item.imageUrl || "",
          imageUrl: item.imageUrl || "",
          sku: item.sku || "",
          description: item.shortDescription || "",
          // Add inStock and quantity properties to fix stock display issues
          inStock: true,
          quantity: 100, // Set a default quantity to prevent max:0 issue
          unlimited: true, // Allow unlimited quantities
          // Note: weight might not be available in the order item
          attributes: item.attributes ? 
            item.attributes.map(attr => ({
              name: attr.name,
              value: attr.value,
              id: 0, // Using a placeholder ID since it's required
              show: 'true', // Add missing 'show' property
              type: 'custom' // Add missing 'type' property
            })) : 
            undefined
        };
        
        // Add to cart with the same quantity as in the original order
        addToCart(product, item.quantity);
      }
      
      // Navigate to cart page
      navigate('/cart');
      
      toast.success('Order items added to cart', { 
        position: 'bottom-right',
        duration: 4000
      });
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to reorder. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Add all items from this order to your cart">
      <span>
        <Button
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          startIcon={loading ? <CircularProgress size={16} /> : <ReplayIcon />}
          onClick={handleReorder}
          disabled={loading}
        >
          Reorder
        </Button>
      </span>
    </Tooltip>
  );
};

export default ReorderButton; 